import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useUser } from "@clerk/react";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { Button } from "../components/shared/Button";
import { EmptyState } from "../components/shared/EmptyState";
import { Modal } from "../components/shared/Modal";
import * as ui from "../styles/ui";
import { api, type NotionHubPage } from "../api/client";
import { parseNotionPageId } from "../lib/notion-url";
import { formatApiError } from "../lib/api-errors";
import { useGitStore } from "../stores/useGitStore";
import { useAuditStore } from "../stores/useAuditStore";
import { useToastStore } from "../stores/useToastStore";
import { useBatchStore } from "../stores/useBatchStore";

export function ImportPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [pages, setPages] = useState<NotionHubPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versioningId, setVersioningId] = useState<string | null>(null);
  const [prModal, setPrModal] = useState<{
    title: string;
    number?: number;
    url?: string;
  } | null>(null);

  const invalidateGit = useGitStore((s) => s.invalidate);
  const addAuditEntry = useAuditStore((s) => s.addEntry);
  const { user } = useUser();
  const actorEmail = user?.primaryEmailAddress?.emailAddress;
  const addToast = useToastStore((s) => s.addToast);
  const batchId = useBatchStore((s) => s.batchId);
  const stageInBatch = useBatchStore((s) => s.stage);
  const refreshBatch = useBatchStore((s) => s.refresh);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.notion
      .search("")
      .then((res) => setPages(res.pages))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const parsedId = useMemo(() => parseNotionPageId(query), [query]);
  const q = query.trim().toLowerCase();

  const filteredPages = useMemo(() => {
    if (!q && !parsedId) return pages;
    return pages.filter((page) => {
      if (parsedId && page.id === parsedId) return true;
      if (!q) return false;
      return page.title.toLowerCase().includes(q);
    });
  }, [pages, q, parsedId]);

  const pastedPage = useMemo(() => {
    if (!parsedId) return null;
    if (pages.some((p) => p.id === parsedId)) return null;
    return { id: parsedId, title: "notion page" };
  }, [parsedId, pages]);

  const handleVersion = useCallback(
    async (pageId: string, title: string) => {
      setVersioningId(pageId);
      try {
        const result = await api.notion.import(pageId, batchId);
        invalidateGit();
        addAuditEntry(
          "import",
          result.alreadyImported ? `opened ${title}` : `imported ${title}`,
          result.path.split("/").slice(0, -1).join("/"),
          "v0.1",
          actorEmail,
        );
        // PR mode + batch: the doc is staged on the shared draft branch. Add it to
        // the floating PR button (one PR for the batch) instead of navigating into
        // the editor, which reads the default branch and would 404.
        if (result.staged) {
          stageInBatch(result.path);
          void refreshBatch();
          addToast("imported — staged for PR", "success");
          return;
        }
        // PR mode without a batch: a per-doc PR was opened; show it centered.
        if (result.pr) {
          addToast(
            result.pr.number
              ? `imported — PR #${result.pr.number} opened`
              : "imported to a PR",
            "success",
          );
          setPrModal({ title, number: result.pr.number, url: result.pr.url });
          return;
        }
        addToast(
          result.alreadyImported ? "opened in shelf" : "imported to git",
          "success",
        );
        navigate(`/doc/${encodeURIComponent(result.path)}`);
      } catch (e) {
        addToast(formatApiError(e), "error");
      } finally {
        setVersioningId(null);
      }
    },
    [
      navigate,
      invalidateGit,
      addAuditEntry,
      addToast,
      batchId,
      stageInBatch,
      refreshBatch,
      actorEmail,
    ],
  );

  return (
    <>
      <TopBar
        title="import"
        actions={<Button onClick={() => navigate("/")}>dashboard</Button>}
      />
      <div style={ui.pageContent}>
        <form onSubmit={(e) => e.preventDefault()} style={styles.searchRow}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="paste notion link or search document hub"
            style={styles.searchInput}
            spellCheck={false}
          />
        </form>

        {error && <p style={styles.errorLine}>{error}</p>}

        {loading ? (
          <div style={styles.loadingWrap}>
            <Spinner size={24} label="loading hub…" />
          </div>
        ) : filteredPages.length === 0 && !pastedPage ? (
          <EmptyState
            title={q || parsedId ? "no documents match" : "document hub empty"}
            body={parsedId ? "paste a valid notion page link" : undefined}
          />
        ) : (
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={ui.thFirst}>name</th>
                <th style={{ ...ui.th, width: 140 }}>categories</th>
                <th style={{ ...ui.th, width: 130 }}>last edited</th>
                <th style={{ ...ui.th, width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {pastedPage && (
                <tr style={ui.row}>
                  <td style={ui.tdFirst}>
                    <span style={styles.pageTitle}>{pastedPage.title}</span>
                  </td>
                  <td style={ui.td}>
                    <span style={ui.metaText}>—</span>
                  </td>
                  <td style={ui.td}>
                    <span style={ui.metaText}>—</span>
                  </td>
                  <td style={ui.td}>
                    <Button
                      variant="primary"
                      onClick={() =>
                        handleVersion(pastedPage.id, pastedPage.title)
                      }
                      disabled={versioningId === pastedPage.id}
                    >
                      {versioningId === pastedPage.id
                        ? "importing..."
                        : "version"}
                    </Button>
                  </td>
                </tr>
              )}
              {filteredPages.map((page) => (
                <tr key={page.id} style={ui.row}>
                  <td style={ui.tdFirst}>
                    <span style={styles.pageTitle}>{page.title}</span>
                  </td>
                  <td style={ui.td}>
                    <span style={ui.metaText}>
                      {(page.categories ?? []).join(", ") || "—"}
                    </span>
                  </td>
                  <td style={ui.td}>
                    <span style={ui.metaText}>
                      {new Date(page.lastEditedTime).toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        },
                      )}
                    </span>
                  </td>
                  <td style={ui.td}>
                    <Button
                      variant="primary"
                      onClick={() => handleVersion(page.id, page.title)}
                      disabled={versioningId === page.id}
                    >
                      {versioningId === page.id ? "importing..." : "version"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {prModal && (
        <Modal
          title="imported to a pull request"
          onClose={() => setPrModal(null)}
          footer={
            <>
              {prModal.url && (
                <Button
                  variant="primary"
                  onClick={() =>
                    window.open(prModal.url, "_blank", "noopener,noreferrer")
                  }
                >
                  {prModal.number ? `view PR #${prModal.number}` : "view PR"}
                </Button>
              )}
              <Button onClick={() => setPrModal(null)}>close</Button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            <strong>{prModal.title}</strong> was imported onto a working branch
            and a pull request was opened against the docs repo. The default
            branch is protected, so it will appear in shelf once the PR is
            merged.
          </p>
        </Modal>
      )}
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  searchRow: {
    marginBottom: 20,
  },
  searchInput: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    padding: "8px 0",
    fontSize: 13,
    fontFamily: "var(--font-mono)",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "40px 0",
  },
  errorLine: {
    color: "var(--comp-salmon)",
    fontSize: 12,
    marginBottom: 16,
  },
  pageTitle: {
    fontSize: 13,
    color: "var(--fg-1)",
  },
};
